import * as React from 'react';

import { Container, FormFeedback, FormGroup, Input, Label, Table } from 'reactstrap';

import * as AccountsApi from '../../api/accounts';
import * as AidsApi from '../../api/aids';
import * as UtilApi from '../../api/util';

import Localization from '../../config/localization';

import BackButton from '../../components/BackButton';
import Loader from '../../components/Loader';

import { GetAddress } from '../../lib/address';
import { GetAidsEffectiveTotal, GetConsumption } from '../../lib/aid';
import Notifications from '../../lib/notifications';
import { GetMonthFromSeason, GetName, ToFloat, ToNumber, ToString } from '../../lib/utils';

import './AssociationAdministration.scss';
import { getQuantumAmount } from '../../lib/money';

class AssociationAdministration extends React.Component<AssociationAdministration.IProps, AssociationAdministration.IState> {
  state: AssociationAdministration.IState = {
    loading: false,
    loadingMessage: '',

    formWasSubmitted: false,

    associations: [],
    selectedMonth: '',

    MONTHS: undefined
  };

  async componentDidMount() {
    const response = await UtilApi.GetMonths();

    const MONTHS = response.data || [];

    await this.setState({ MONTHS, selectedMonth: MONTHS[0] });

    await $('#selectedMonth').selectmenu({ change: this._selectMonth });

    return this._selectMonth();
  }

  renderAidRow(aid: Aid.IAid) {
    if (!aid) {
      return null;
    }

    aid.consumption = aid.consumption === '' ? '' : ToFloat(aid.consumption);

    return (
      <tr key={aid.aidId}>
        <td className="align-middle">{GetName({ firstName: aid.firstName, lastName: aid.lastName })}</td>
        <td className="align-middle">{aid.personalIdentificationNumber}</td>
        <td className="text-center align-middle">{getQuantumAmount(aid)}</td>
        <td className="text-center align-middle">{aid.approvalDocumentNo}</td>
        <td className="text-center align-middle">{aid.familyMembersNo}</td>
        <td className="text-center align-middle">{aid.memberFamilyIncome}</td>
        <td className="remove-arrow position-relative text-center align-middle">
          <Input
            type="number"
            name="consumption"
            id="consumption"
            step="0.01"
            value={aid.consumption}
            onChange={event => {
              const element = event.currentTarget || event.target || {};

              aid.consumption = element.value === '' ? '' : ToFloat(element.value);
              this.forceUpdate();
            }}
            className="eon-input-text consumption text-center"
            invalid={!aid.consumption && aid.consumption !== 0}
          />
        </td>
        <td className="strong text-center align-middle">{GetConsumption(aid)}</td>
      </tr>
    );
  }

  renderAidsTable(aids: Aid.IAid[]) {
    if (!aids) {
      return null;
    }

    return (
      <div>
        <Table size="sm" className="m-0">
          <thead>
          <tr className="text-uppercase">
            <th className="font-weight-normal">{Localization.associationAdministrationTableHeadName}</th>
            <th
              className="font-weight-normal">{Localization.associationAdministrationTableHeadPersonalIdentificationNumber}</th>
            <th className="text-center font-weight-normal">{Localization.associationAdministrationTableHeadQuantum}</th>
            <th
              className="text-center font-weight-normal">{Localization.associationAdministrationTableHeadApprovalDocumentNo}</th>
            <th className="text-center font-weight-normal">{Localization.associationAdministrationTableHeadMembers}</th>
            <th
              className="text-center font-weight-normal">{Localization.associationAdministrationTableHeadMemberIncome}</th>
            <th className="text-center font-weight-normal">{Localization.associationAdministrationTableHeadConsum}</th>
            <th
              className="text-center font-weight-normal">{Localization.associationAdministrationTableHeadEfectiveQuantum}</th>
          </tr>
          </thead>
          <tbody>{aids.map(aid => this.renderAidRow(aid))}</tbody>
          <tfoot>
          <tr className="bg-eon-ultra-light-grey text-center">
            <td />
            <td />
            <td />
            <td />
            <td />
            <td className="align-middle">
              <button className="eon-button bg-eon-red eon-white small" onClick={() => this._saveConsumption(aids)}>
                <span>{Localization.associationAdministrationTableSave}</span>
              </button>
            </td>
            <td className="align-middle strong">{Localization.associationAdministrationTableTotal.toUpperCase()}</td>
            <td className="align-middle strong">{GetAidsEffectiveTotal(aids)}</td>
          </tr>
          </tfoot>
        </Table>
      </div>
    );
  }

  renderAssociationRow(association: Aid.IAssociation) {
    return association.consumptionPoints.map((consumptionPoint, index) => (
      <div className="mt-5" key={consumptionPoint.consumptionPointCode}>
        <Label className="bg-eon-ultra-light-grey px-2 py-1 w-100 d-flex">
          <h4 className="eon-quote eon-turquoise mr-auto">
            {!index
              ? `${association.partnerCode} ${GetName({
                firstName: association.firstName,
                lastName: association.lastName
              })}`
              : ''}
          </h4>

          <div className="ml-auto">
            <p className="eon-caption eon-red strong">{consumptionPoint.consumptionPointCode}</p>
            <h4 className="eon-quote">{GetAddress(consumptionPoint.address)}</h4>
          </div>
        </Label>
        {this.renderAidsTable(consumptionPoint.aids)}
      </div>
    ));
  }

  renderAssociations() {
    return this.state.associations.map(association => this.renderAssociationRow(association));
  }

  renderMonthsOptions() {
    if (!this.state.MONTHS) {
      return null;
    }

    return this.state.MONTHS.map(month => {
      if (!month) {
        return null;
      }

      return (
        <option key={month} value={month}>
          {Localization.Months[GetMonthFromSeason(month)]}
        </option>
      );
    });
  }

  render() {
    return (
      <Container className="association-administration px-5 py-4 my-4">
        <BackButton />

        <Loader loading={this.state.loading} loadingMessage={this.state.loadingMessage} />

        <h4 className="mt-5 eon-title  eon-red force-wrap">{Localization.associationAdministrationTitle}</h4>
        <h5 className="mt-3 eon-title  eon-middle-grey force-wrap">{Localization.associationAdministrationInfo}</h5>

        <FormGroup className="w-50 mt-2">
          <fieldset className="eon-select large eon-red">
            <Input
              type="select"
              name="selectedMonth"
              id="selectedMonth"
              value={this.state.selectedMonth}
              onChange={this._selectMonth}
              className="eon-select large eon-red"
              invalid={!this.state.selectedMonth && this.state.formWasSubmitted}
            >
              {this.renderMonthsOptions()}
            </Input>
            <FormFeedback tooltip>{Localization.associationAdministrationMonthError}</FormFeedback>
          </fieldset>
        </FormGroup>

        {this.renderAssociations()}
      </Container>
    );
  }

  _selectMonth = async (event?: Event | React.ChangeEvent<HTMLInputElement>) => {
    try {
      if (event) {
        event.preventDefault();
      }

      const element = event ? event.target || event.currentTarget || {} : undefined;

      const data = { loading: true } as AssociationAdministration.IState;

      if (element) {
        data.selectedMonth = ToString($(element).val());
      }

      await this.setState(data);

      const response = await AccountsApi.GetAssociationsFromMonth(this.state.selectedMonth);

      return this.setState({ associations: response.data, loading: false });
    } catch (error) {
      Notifications.error(error && error.message);

      return this.setState({ loading: false });
    }
  };

  _saveConsumption = async (aids: Aid.IAid[]) => {
    try {
      await this.setState({ loading: true });

      const data = aids.map(aid => ({
        aidId: aid.aidId,
        consumption: ToNumber(aid.consumption)
      }));

      await AidsApi.SaveConsumption(data);

      return this.setState({ loading: false });
    } catch (error) {
      Notifications.error(error && error.message);

      return this.setState({ loading: false });
    }
  };
}

export default AssociationAdministration;
